import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHome, FaFileInvoice, FaBarcode, FaSave, FaSpinner, FaTrash, FaEdit, FaCog } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axios from 'axios'; // Importa o axios para fazer as chamadas de API
import {
    Container,
    Typography,
    Grid,
    Paper,
    Switch,
    FormControlLabel,
    TextField,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
} from '@mui/material';

const ConfigurationScreen = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('notas');
    const [loading, setLoading] = useState(false);
    const [loadingBoletos, setLoadingBoletos] = useState(false);
    const [editingBoletoId, setEditingBoletoId] = useState(null);

    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;

    const [nfsConfigs, setNfsConfigs] = useState({
        serviceCode: '',
        issqnRate: '',
        taxRegime: '',
        prefeituraLogin: '',
        prefeituraSenha: '',
        proximaNfse: '',
        tokenNfseHomologacao: '',
        tokenNfseProducao: '',
        inscricaoMunicipal: '',
    });

    const [boletoConfigs, setBoletoConfigs] = useState({
        banco: '',
        posto: '',
        codigoBeneficiario: '',
        apiKey: '',
        accessToken: '',
        codigoAcesso: '',
        nomeUsuario: '',
        password: '',
        caminhoPdf: '',
        caminhoXml: '',
        urlBase: '',
        agency: '',
        account: '',
        instructions: ''
    });

    const [boletoConfigsList, setBoletoConfigsList] = useState([]);

    const [boletoControlConfigs, setBoletoControlConfigs] = useState({
        // Avisos de Vencimento
        aviso1Dia: false,
        aviso3Dias: false,
        aviso5Dias: false,
        avisoVencido: false,
        msgAvisoAVencer: 'Olá, [Nome do Cliente]. Gostaríamos de lembrar que o seu boleto no valor de [Valor do Boleto] vence amanhã, dia [Data de Vencimento].',
        msgAvisoVencido: 'Olá, [Nome do Cliente]. O seu boleto no valor de [Valor do Boleto] venceu no dia [Data de Vencimento]. Por favor, regularize o pagamento.',

        // Automações
        gerarBoletosAutomaticos: false,
        diaGeracao: '1', // Default to day 1
        baixaAutomatica: false,
        enviarEmail: false,
        enviarWhatsapp: false,
        tokenWhatsapp: '',
        msgEmailPadrao: 'Olá, [Nome do Cliente]. Segue em anexo o seu boleto com vencimento em [Data de Vencimento]. Obrigado.',
        msgWhatsappPadrao: 'Olá, [Nome do Cliente]. Segue o link para o seu boleto: [Link do Boleto]. Vencimento em [Data de Vencimento].'
    });

    // Estado inicial para as configurações do formulário
    const [config, setConfig] = useState({
        aviso_0_dia: false,
        aviso_1_dia: false,
        aviso_3_dias: false,
        aviso_5_dias: false,
        aviso_vencido: false,
        msg_aviso_a_vencer: '',
        msg_aviso_vence_hoje: '',
        msg_aviso_vencido: '',
        gerar_boletos_automaticos: false,
        dia_geracao: 1,
        baixa_automatica: false,
        enviar_email: false,
        enviar_whatsapp: false,
        token_whatsapp: '',
        api_whatsapp: '',
        msg_email_padrao: '',
        msg_whatsapp_padrao: '',
    });

    // Estados para gerenciar o feedback do usuário e o carregamento
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });



    // Função para buscar as configurações de boleto do backend
    const fetchBoletoConfigs = async () => {
        setLoadingBoletos(true);
        try {
            const response = await axios.get(`${URL_BACKEND}/config_boletos`);
            setBoletoConfigsList(response.data);
        } catch (error) {
            console.error('Erro ao buscar configurações de boletos:', error);
            toast.error('Erro ao carregar configurações de boletos.');
        } finally {
            setLoadingBoletos(false);
        }
    };

    // Função para carregar as configurações do servidor ao montar o componente
    const fetchConfig = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${URL_BACKEND}/boleto_controls`);
            // garante que existam todas as chaves e em tipos corretos
            setConfig(prev => ({
                ...prev,
                // booleans
                aviso_0_dia: !!Number(data.aviso_0_dia),
                aviso_1_dia: !!Number(data.aviso_1_dia),
                aviso_3_dias: !!Number(data.aviso_3_dias),
                aviso_5_dias: !!Number(data.aviso_5_dias),
                aviso_vencido: !!Number(data.aviso_vencido),
                gerar_boletos_automaticos: !!Number(data.gerar_boletos_automaticos),
                baixa_automatica: !!Number(data.baixa_automatica),
                enviar_email: !!Number(data.enviar_email),
                enviar_whatsapp: !!Number(data.enviar_whatsapp),

                // números / strings
                dia_geracao: Number(data.dia_geracao) || 1,
                api_whatsapp: data.api_whatsapp ?? '',
                token_whatsapp: data.token_whatsapp ?? '',
                msg_email_padrao: data.msg_email_padrao ?? '',
                msg_whatsapp_padrao: data.msg_whatsapp_padrao ?? '',
                msg_aviso_a_vencer: data.msg_aviso_a_vencer ?? '',
                msg_aviso_vence_hoje: data.msg_aviso_vence_hoje ?? '',
                msg_aviso_vencido: data.msg_aviso_vencido ?? '',
            }));
        } catch (error) {
            console.error('Erro ao carregar as configurações:', error);
            setSnackbar({ open: true, message: 'Erro ao carregar as configurações. Tente novamente.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };


    // Efeito para carregar as configurações de boleto quando a aba for ativada
    useEffect(() => {
        if (activeTab === 'boletos') {
            fetchBoletoConfigs();
        }
        if (activeTab === 'controleBoletos') {
            fetchConfig();
        }
    }, [activeTab]);

    // Função para salvar uma nova configuração ou atualizar uma existente
    const handleSaveConfigs = async () => {
        setLoading(true);
        const toastId = toast.loading('Salvando configurações...');

        // Mapeia os dados do estado para o formato snake_case do backend
        const dataToSend = {
            banco: boletoConfigs.banco,
            agencia: boletoConfigs.agency,
            conta: boletoConfigs.account,
            posto: boletoConfigs.posto,
            codigo_beneficiario: boletoConfigs.codigoBeneficiario,
            api_key: boletoConfigs.apiKey,
            access_token: boletoConfigs.accessToken,
            codigo_acesso: boletoConfigs.codigoAcesso,
            nome_usuario: boletoConfigs.nomeUsuario,
            password: boletoConfigs.password,
            caminho_pdf: boletoConfigs.caminhoPdf,
            caminho_xml: boletoConfigs.caminhoXml,
            url_base: boletoConfigs.urlBase,
            instrucoes: boletoConfigs.instructions,
        };

        try {
            if (editingBoletoId) {
                await axios.put(`${URL_BACKEND}/config_boletos/${editingBoletoId}`, dataToSend);
            } else {
                await axios.post(`${URL_BACKEND}/config_boletos`, dataToSend);
            }

            toast.success('Configurações salvas com sucesso!', { id: toastId });
            fetchBoletoConfigs(); // Recarrega a lista após o salvamento

            // Limpa o formulário e o estado de edição
            setBoletoConfigs({
                banco: '', posto: '', codigoBeneficiario: '', apiKey: '', accessToken: '',
                codigoAcesso: '', nomeUsuario: '', password: '', caminhoPdf: '',
                caminhoXml: '', urlBase: '', agency: '', account: '', instructions: ''
            });
            setEditingBoletoId(null);
        } catch (error) {
            console.error('Erro ao salvar configurações:', error);
            toast.error('Erro ao salvar as configurações.', { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    // Função para salvar as novas configurações de controle de boleto
    const handleSaveBoletoControlConfigs = () => {
        setLoading(true);
        const toastId = toast.loading('Salvando configurações de controle...');

        // Como o backend ainda não existe, apenas simula o salvamento
        setTimeout(() => {
            console.log('Novas configurações de controle de boleto salvas:', boletoControlConfigs);
            setLoading(false);
            toast.success('Configurações de controle salvas com sucesso!', { id: toastId });
        }, 1000);
    };

    const handleChangeNfs = (e) => {
        const { name, value } = e.target;
        setNfsConfigs(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeBoleto = (e) => {
        const { name, value } = e.target;
        setBoletoConfigs(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeBoletoControl = (e) => {
        const { name, value, type, checked } = e.target;
        setBoletoControlConfigs(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleEditBoleto = (config) => {
        // Converte os dados do backend para o formato camelCase do frontend
        setBoletoConfigs({
            banco: config.banco,
            posto: config.posto,
            codigoBeneficiario: config.codigo_beneficiario,
            apiKey: config.api_key,
            accessToken: config.access_token,
            codigoAcesso: config.codigo_acesso,
            nomeUsuario: config.nome_usuario,
            password: config.password,
            caminhoPdf: config.caminho_pdf,
            caminhoXml: config.caminho_xml,
            urlBase: config.url_base,
            agency: config.agencia,
            account: config.conta,
            instructions: config.instrucoes
        });
        setEditingBoletoId(config.id);
        toast('Formulário preenchido com os dados para edição.', { icon: '📝' });
    };

    const handleDeleteBoleto = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir esta configuração?')) {
            const toastId = toast.loading('Excluindo...');
            try {
                await axios.delete(`${URL_BACKEND}/config_boletos/${id}`);
                toast.success('Configuração excluída com sucesso.', { id: toastId });
                fetchBoletoConfigs(); // Recarrega a lista após a exclusão
            } catch (error) {
                console.error('Erro ao excluir configuração:', error);
                toast.error('Erro ao excluir a configuração.', { id: toastId });
            }
        }
    };

    // Função para lidar com a mudança nos campos do formulário
    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setConfig((prevConfig) => ({
            ...prevConfig,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // Função para salvar as configurações no back-end
    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.post(`${URL_BACKEND}/boleto_controls`, config);
            setSnackbar({
                open: true,
                message: 'Configurações salvas com sucesso!',
                severity: 'success',
            });
        } catch (error) {
            console.error('Erro ao salvar as configurações:', error);
            setSnackbar({
                open: true,
                message: 'Erro ao salvar as configurações. Verifique o console para mais detalhes.',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    // Função para fechar o Snackbar
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Exibe o CircularProgress enquanto as configurações estão sendo carregadas
    if (loading) {
        return (
            <Container style={{ marginTop: 32 }}>
                <Grid container justifyContent="center" alignItems="center" style={{ height: '80vh' }}>
                    <CircularProgress />
                    <Typography variant="h6" style={{ marginLeft: 16 }}>
                        Carregando configurações...
                    </Typography>
                </Grid>
            </Container>
        );
    }

    const diasDoMes = Array.from({ length: 28 }, (_, i) => i + 1);

    return (
        <div style={ui.page}>
            {/* Header com gradiente */}
            <div style={ui.headerBar}>
                <h1 style={ui.title}>Configurações do Sistema</h1>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={ui.backButton}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    <FaHome style={{ marginRight: 8 }} /> Voltar
                </button>
            </div>

            <div style={ui.contentCard}>
                {/* Abas */}
                <div style={ui.tabsContainer}>
                    <button
                        onClick={() => setActiveTab('notas')}
                        style={{ ...ui.tabButton, ...(activeTab === 'notas' && ui.activeTab) }}
                    >
                        <FaFileInvoice style={{ marginRight: 8 }} /> Configurações de NFS-e
                    </button>
                    <button
                        onClick={() => setActiveTab('boletos')}
                        style={{ ...ui.tabButton, ...(activeTab === 'boletos' && ui.activeTab) }}
                    >
                        <FaBarcode style={{ marginRight: 8 }} /> Dados Bancários
                    </button>
                    <button
                        onClick={() => setActiveTab('controleBoletos')}
                        style={{ ...ui.tabButton, ...(activeTab === 'controleBoletos' && ui.activeTab) }}
                    >
                        <FaCog style={{ marginRight: 8 }} /> Controle de Boletos
                    </button>
                </div>

                {/* Conteúdo das abas */}
                <div style={ui.tabContent}>
                    {activeTab === 'notas' && (
                        <div>
                            <h3 style={ui.sectionTitle}>Parâmetros para Geração de NFS-e</h3>
                            <p style={ui.sectionDesc}>Defina as configurações fiscais e de serviço para emissão automática de notas fiscais.</p>
                            <div style={ui.formGrid}>
                                <div style={ui.formField}>
                                    <label htmlFor="prefeituraLogin" style={ui.formLabel}>Login da Prefeitura</label>
                                    <input type="text" id="prefeituraLogin" name="prefeituraLogin" value={nfsConfigs.prefeituraLogin} onChange={handleChangeNfs} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="prefeituraSenha" style={ui.formLabel}>Senha da Prefeitura</label>
                                    <input type="password" id="prefeituraSenha" name="prefeituraSenha" value={nfsConfigs.prefeituraSenha} onChange={handleChangeNfs} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="inscricaoMunicipal" style={ui.formLabel}>Inscrição Municipal</label>
                                    <input type="text" id="inscricaoMunicipal" name="inscricaoMunicipal" value={nfsConfigs.inscricaoMunicipal} onChange={handleChangeNfs} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="proximaNfse" style={ui.formLabel}>Próxima NFSe</label>
                                    <input type="text" id="proximaNfse" name="proximaNfse" value={nfsConfigs.proximaNfse} onChange={handleChangeNfs} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="tokenNfseHomologacao" style={ui.formLabel}>Token NFSe Homologação</label>
                                    <input type="text" id="tokenNfseHomologacao" name="tokenNfseHomologacao" value={nfsConfigs.tokenNfseHomologacao} onChange={handleChangeNfs} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="tokenNfseProducao" style={ui.formLabel}>Token NFSe Produção</label>
                                    <input type="text" id="tokenNfseProducao" name="tokenNfseProducao" value={nfsConfigs.tokenNfseProducao} onChange={handleChangeNfs} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="serviceCode" style={ui.formLabel}>Código do Serviço</label>
                                    <input type="text" id="serviceCode" name="serviceCode" value={nfsConfigs.serviceCode} onChange={handleChangeNfs} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="issqnRate" style={ui.formLabel}>Alíquota ISSQN (%)</label>
                                    <input type="number" id="issqnRate" name="issqnRate" value={nfsConfigs.issqnRate} onChange={handleChangeNfs} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="taxRegime" style={ui.formLabel}>Regime Tributário</label>
                                    <select id="taxRegime" name="taxRegime" value={nfsConfigs.taxRegime} onChange={handleChangeNfs} style={ui.select}>
                                        <option value="">Selecione...</option>
                                        <option value="simples">Simples Nacional</option>
                                        <option value="lucro_presumido">Lucro Presumido</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'boletos' && (
                        <div>
                            <h3 style={ui.sectionTitle}>Parâmetros para Geração de Boletos</h3>
                            <p style={ui.sectionDesc}>Configure os dados bancários e instruções para a emissão dos boletos de cobrança.</p>
                            <div style={ui.formGrid}>
                                <div style={ui.formField}>
                                    <label htmlFor="banco" style={ui.formLabel}>Nome do Banco</label>
                                    <input type="text" id="banco" name="banco" value={boletoConfigs.banco} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="agency" style={ui.formLabel}>Agência</label>
                                    <input type="text" id="agency" name="agency" value={boletoConfigs.agency} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="account" style={ui.formLabel}>Conta</label>
                                    <input type="text" id="account" name="account" value={boletoConfigs.account} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="posto" style={ui.formLabel}>Posto</label>
                                    <input type="text" id="posto" name="posto" value={boletoConfigs.posto} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="codigoBeneficiario" style={ui.formLabel}>Código do Beneficiário</label>
                                    <input type="text" id="codigoBeneficiario" name="codigoBeneficiario" value={boletoConfigs.codigoBeneficiario} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="apiKey" style={ui.formLabel}>API Key</label>
                                    <input type="text" id="apiKey" name="apiKey" value={boletoConfigs.apiKey} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="accessToken" style={ui.formLabel}>Access Token</label>
                                    <input type="text" id="accessToken" name="accessToken" value={boletoConfigs.accessToken} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="codigoAcesso" style={ui.formLabel}>Código de Acesso</label>
                                    <input type="text" id="codigoAcesso" name="codigoAcesso" value={boletoConfigs.codigoAcesso} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="nomeUsuario" style={ui.formLabel}>Nome de Usuário</label>
                                    <input type="text" id="nomeUsuario" name="nomeUsuario" value={boletoConfigs.nomeUsuario} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="password" style={ui.formLabel}>Password</label>
                                    <input type="password" id="password" name="password" value={boletoConfigs.password} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="caminhoPdf" style={ui.formLabel}>Caminho do PDF</label>
                                    <input type="text" id="caminhoPdf" name="caminhoPdf" value={boletoConfigs.caminhoPdf} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="caminhoXml" style={ui.formLabel}>Caminho do XML</label>
                                    <input type="text" id="caminhoXml" name="caminhoXml" value={boletoConfigs.caminhoXml} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={ui.formField}>
                                    <label htmlFor="urlBase" style={ui.formLabel}>URL Base</label>
                                    <input type="text" id="urlBase" name="urlBase" value={boletoConfigs.urlBase} onChange={handleChangeBoleto} style={ui.input} />
                                </div>
                                <div style={{ ...ui.formField, gridColumn: 'span 3' }}>
                                    <label htmlFor="instructions" style={ui.formLabel}>Instruções de Pagamento</label>
                                    <textarea id="instructions" name="instructions" value={boletoConfigs.instructions} onChange={handleChangeBoleto} style={ui.textarea} rows="4"></textarea>
                                </div>
                            </div>

                            <div style={ui.saveButtonContainer}>
                                <button
                                    onClick={handleSaveConfigs}
                                    disabled={loading}
                                    style={{ ...ui.saveButton, ...(loading && ui.saveButtonDisabled) }}
                                >
                                    {loading ? (
                                        <>
                                            <FaSpinner className="animate-spin" style={{ marginRight: 8 }} /> Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave style={{ marginRight: 8 }} /> {editingBoletoId ? 'Atualizar Configuração' : 'Salvar Configuração'}
                                        </>
                                    )}
                                </button>
                            </div>

                            <hr style={ui.divider} />

                            {/* Tabela de Configurações Existentes */}
                            <h3 style={ui.sectionTitle}>Configurações Existentes</h3>
                            <p style={ui.sectionDesc}>Visualize e gerencie as configurações de boleto já salvas.</p>

                            {loadingBoletos ? (
                                <p style={{ textAlign: 'center' }}>Carregando configurações...</p>
                            ) : (
                                <div style={ui.tableWrapper}>
                                    <table style={ui.table}>
                                        <thead>
                                            <tr>
                                                <th style={ui.tableHeader}>ID</th>
                                                <th style={ui.tableHeader}>Banco</th>
                                                <th style={ui.tableHeader}>Cód. Beneficiário</th>
                                                <th style={ui.tableHeader}>Agência/Conta</th>
                                                <th style={ui.tableHeader}>URL Base</th>
                                                <th style={ui.tableHeader}>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {boletoConfigsList.map((config) => (
                                                <tr key={config.id} style={ui.tableRow}>
                                                    <td style={ui.tableCell}>{config.id}</td>
                                                    <td style={ui.tableCell}>{config.banco}</td>
                                                    <td style={ui.tableCell}>{config.codigo_beneficiario}</td>
                                                    <td style={ui.tableCell}>{config.agencia} / {config.conta}</td>
                                                    <td style={ui.tableCell}>{config.url_base}</td>
                                                    <td style={ui.tableCell}>
                                                        <button onClick={() => handleEditBoleto(config)} style={ui.actionButton} title="Editar">
                                                            <FaEdit />
                                                        </button>
                                                        <button onClick={() => handleDeleteBoleto(config.id)} style={{ ...ui.actionButton, color: '#ef4444' }} title="Excluir">
                                                            <FaTrash />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                        </div>
                    )}

                    {activeTab === 'controleBoletos' && (
                        <div>
                            <h3 style={ui.sectionTitle}>Avisos e Notificações</h3>
                            <p style={ui.sectionDesc}>Configure o envio de avisos de vencimento e boletos vencidos.</p>

                            <div style={ui.controlSection}>
                                <div style={ui.toggleGrid}>
                                    <div style={ui.toggleRow}>
                                        <label htmlFor="aviso_0_dia" style={ui.toggleLabel}>Avisar no vencimento</label>
                                        <input
                                            type="checkbox"
                                            id="aviso_0_dia"
                                            name="aviso_0_dia"
                                            checked={!!config.aviso_0_dia}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div style={ui.toggleRow}>
                                        <label htmlFor="aviso_1_dia" style={ui.toggleLabel}>Aviso de vencimento 1 dia antes</label>
                                        <input
                                            type="checkbox"
                                            id="aviso_1_dia"
                                            name="aviso_1_dia"
                                            checked={!!config.aviso_1_dia}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div style={ui.toggleRow}>
                                        <label htmlFor="aviso_3_dias" style={ui.toggleLabel}>Aviso de vencimento 3 dias antes</label>
                                        <input
                                            type="checkbox"
                                            id="aviso_3_dias"
                                            name="aviso_3_dias"
                                            checked={!!config.aviso_3_dias}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div style={ui.toggleRow}>
                                        <label htmlFor="aviso_5_dias" style={ui.toggleLabel}>Aviso de vencimento 5 dias antes</label>
                                        <input
                                            type="checkbox"
                                            id="aviso_5_dias"
                                            name="aviso_5_dias"
                                            checked={!!config.aviso_5_dias}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div style={ui.toggleRow}>
                                        <label htmlFor="aviso_vencido" style={ui.toggleLabel}>Aviso de boleto vencido</label>
                                        <input
                                            type="checkbox"
                                            id="aviso_vencido"
                                            name="aviso_vencido"
                                            checked={!!config.aviso_vencido}
                                            onChange={handleChange}
                                        />
                                    </div>

                                </div>

                                <div style={{ ...ui.formGrid, marginTop: '24px' }}>
                                    <div style={{ ...ui.formField, gridColumn: 'span 2' }}>
                                        <label htmlFor="msgAvisoAVencer" style={ui.formLabel}>Mensagem padrão para avisos de vencimento (*A Vencer*)</label>
                                        <textarea id="msg_aviso_a_vencer" name="msg_aviso_a_vencer" value={config.msg_aviso_a_vencer} onChange={handleChange} style={ui.textarea} rows="10"></textarea>
                                    </div>
                                    <div style={{ ...ui.formField, gridColumn: 'span 2' }}>
                                        <label htmlFor="msgAvisoAVencerHoje" style={ui.formLabel}>Mensagem padrão para avisos de vencimento (*HOJE*)</label>
                                        <textarea id="msg_aviso_vence_hoje" name="msg_aviso_vence_hoje" value={config.msg_aviso_vence_hoje} onChange={handleChange} style={ui.textarea} rows="10"></textarea>
                                    </div>
                                    <div style={{ ...ui.formField, gridColumn: 'span 2' }}>
                                        <label htmlFor="msgAvisoVencido" style={ui.formLabel}>Mensagem padrão para avisos de boleto vencido</label>
                                        <textarea id="msg_aviso_vencido" name="msg_aviso_vencido" value={config.msg_aviso_vencido} onChange={handleChange} style={ui.textarea} rows="10"></textarea>
                                    </div>
                                </div>
                            </div>

                            <hr style={ui.divider} />

                            <h3 style={ui.sectionTitle}>Automação e Envio</h3>
                            <p style={ui.sectionDesc}>Configure a geração, baixa e envio automático de boletos.</p>

                            <div style={ui.controlSection}>
                                <div style={ui.toggleGrid}>
                                    <div style={ui.toggleRow}>
                                        <label htmlFor="gerar_boletos_automaticos" style={ui.toggleLabel}>Gerar boletos automáticos</label>
                                        <input
                                            type="checkbox"
                                            id="gerar_boletos_automaticos"
                                            name="gerar_boletos_automaticos"
                                            checked={!!config.gerar_boletos_automaticos}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    {config.gerar_boletos_automaticos && (
                                        <div style={ui.formField}>
                                            <label htmlFor="dia_geracao" style={ui.formLabel}>Dia para geração dos boletos</label>
                                            <select id="dia_geracao" name="dia_geracao" value={config.dia_geracao} onChange={handleChange} style={ui.select}>
                                                {diasDoMes.map(dia => (<option key={dia} value={dia}>{dia}</option>))}
                                            </select>
                                        </div>
                                    )}
                                    <div style={ui.toggleRow}>
                                        <label htmlFor="baixa_automatica" style={ui.toggleLabel}>Baixa automática de boletos diariamente</label>
                                        <input
                                            type="checkbox"
                                            id="baixa_automatica"
                                            name="baixa_automatica"
                                            checked={!!config.baixa_automatica}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div style={ui.toggleRow}>
                                        <label htmlFor="enviar_email" style={ui.toggleLabel}>Envio automático de boletos via e-mail</label>
                                        <input
                                            type="checkbox"
                                            id="enviar_email"
                                            name="enviar_email"
                                            checked={!!config.enviar_email}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div style={ui.toggleRow}>
                                        <label htmlFor="enviar_whatsapp" style={ui.toggleLabel}>Envio automático de boletos via WhatsApp</label>
                                        <input
                                            type="checkbox"
                                            id="enviar_whatsapp"
                                            name="enviar_whatsapp"
                                            checked={!!config.enviar_whatsapp}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div style={ui.formField}>
                                        <label htmlFor="api_whatsapp" style={ui.formLabel}>API WhatsApp</label>
                                        <input type="text" id="api_whatsapp" name="api_whatsapp"
                                            value={config.api_whatsapp} onChange={handleChange} style={ui.input} />
                                    </div>
                                    <div style={ui.formField}>
                                        <label htmlFor="token_whatsapp" style={ui.formLabel}>Token da API de envio do WhatsApp</label>
                                        <input type="text" id="token_whatsapp" name="token_whatsapp"
                                            value={config.token_whatsapp} onChange={handleChange} style={ui.input} />
                                    </div>
                                </div>
                                <div style={{ ...ui.formGrid, marginTop: '24px' }}>
                                    <div style={{ ...ui.formField, gridColumn: 'span 2' }}>
                                        <label htmlFor="msgEmailPadrao" style={ui.formLabel}>Mensagem padrão para envio no e-mail</label>
                                        <textarea id="msg_email_padrao" name="msg_email_padrao" value={config.msg_email_padrao} onChange={handleChange} style={ui.textarea} rows="8"></textarea>
                                    </div>
                                    {/* <div style={{ ...ui.formField, gridColumn: 'span 2' }}>
                                        <label htmlFor="msgWhatsappPadrao" style={ui.formLabel}>Mensagem padrão para envio no WhatsApp</label>
                                        <textarea id="msg_whatsapp_padrao" name="msg_whatsapp_padrao" value={config.msg_whatsapp_padrao} onChange={handleChange} style={ui.textarea} rows="4"></textarea>
                                    </div> */}
                                </div>
                            </div>

                            <div style={ui.saveButtonContainer}>
                                <button onClick={handleSave} disabled={saving} style={{ ...ui.saveButton, ...(saving && ui.saveButtonDisabled) }}>
                                    {saving ? (<> <FaSpinner className="animate-spin" style={{ marginRight: 8 }} /> Salvando... </>) : (<> <FaSave style={{ marginRight: 8 }} /> Salvar Configurações de Controle </>)}
                                </button>
                            </div>
                        </div>
                    )}

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbar.severity}
                            variant="filled"
                            sx={{ width: '100%' }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>


                </div>
            </div>
        </div>
    );
};

// 🎨 UI tokens (mantidos e adicionados)
const ui = {
    page: {
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        padding: 24,
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
        color: '#111827',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        boxSizing: 'border-box',
        marginBottom: 40
    },
    headerBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 24,
        marginBottom: 24,
        borderRadius: 16,
        border: '1px solid #e5e7eb',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
    },
    title: { margin: 0, fontSize: '1.75rem', fontWeight: 700, letterSpacing: 0.2 },
    backButton: {
        backgroundColor: 'rgba(255,255,255,0.15)',
        color: '#fff',
        border: '2px solid rgba(255,255,255,0.25)',
        padding: '10px 16px',
        borderRadius: 12,
        fontWeight: 700,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        transition: 'transform .15s ease'
    },
    contentCard: {
        backgroundColor: '#fff',
        borderRadius: 14,
        boxShadow: '0 6px 16px rgba(0,0,0,0.06)',
        padding: 24,
    },
    tabsContainer: {
        display: 'flex',
        borderBottom: '1px solid #e5e7eb',
        marginBottom: 24
    },
    tabButton: {
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: '2px solid transparent',
        padding: '12px 24px',
        fontSize: '0.875rem',
        fontWeight: 700,
        color: '#6b7280',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        transition: 'color .2s ease, border-color .2s ease',
    },
    activeTab: {
        color: '#4f46e5',
        borderBottomColor: '#4f46e5'
    },
    tabContent: {
        padding: '24px 0',
    },
    sectionTitle: {
        margin: '0 0 8px 0',
        fontSize: '1.125rem',
        fontWeight: 700,
        color: '#1f2937'
    },
    sectionDesc: {
        margin: '0 0 24px 0',
        fontSize: '0.875rem',
        color: '#6b7280'
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 24
    },
    formField: {
        display: 'flex',
        flexDirection: 'column'
    },
    formLabel: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '#374151',
        marginBottom: 4
    },
    input: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '0.875rem',
        borderRadius: 8,
        border: '1px solid #d1d5db',
        transition: 'border-color .2s ease, box-shadow .2s ease',
        boxSizing: 'border-box'
    },
    select: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '0.875rem',
        borderRadius: 8,
        border: '1px solid #d1d5db',
        transition: 'border-color .2s ease, box-shadow .2s ease',
        boxSizing: 'border-box',
        backgroundColor: '#fff'
    },
    textarea: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '0.875rem',
        borderRadius: 8,
        border: '1px solid #d1d5db',
        transition: 'border-color .2s ease, box-shadow .2s ease',
        boxSizing: 'border-box'
    },
    saveButtonContainer: {
        marginTop: 24,
        paddingTop: 24,
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'flex-end'
    },
    saveButton: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 24px',
        fontSize: '0.875rem',
        fontWeight: 700,
        color: '#fff',
        backgroundColor: '#4f46e5',
        borderRadius: 8,
        border: 'none',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
        transition: 'background-color .2s ease, transform .2s ease, box-shadow .2s ease'
    },
    saveButtonDisabled: {
        backgroundColor: '#a5b4fc',
        cursor: 'not-allowed',
        boxShadow: 'none',
        transform: 'none'
    },
    divider: {
        height: 1,
        backgroundColor: '#e5e7eb',
        border: 'none',
        margin: '40px 0'
    },
    tableWrapper: {
        overflowX: 'auto',
        borderRadius: 8,
        border: '1px solid #e5e7eb'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        textAlign: 'left'
    },
    tableHeader: {
        padding: 12,
        backgroundColor: '#f9fafb',
        borderBottom: '2px solid #e5e7eb',
        color: '#4b5563',
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '.75rem'
    },
    tableRow: {
        borderBottom: '1px solid #e5e7eb',
        transition: 'background-color .2s ease',
        ':hover': {
            backgroundColor: '#f3f4f6'
        }
    },
    tableCell: {
        padding: 12,
        fontSize: '.875rem',
        color: '#374151'
    },
    actionButton: {
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#4b5563',
        fontSize: '1rem',
        margin: '0 4px',
        transition: 'color .2s ease',
        ':hover': {
            color: '#4f46e5'
        }
    },
    // Novos estilos para a seção de controle
    controlSection: {
        backgroundColor: '#f9fafb',
        borderRadius: 12,
        padding: 24,
        border: '1px solid #e5e7eb'
    },
    toggleGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 16
    },
    toggleRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 0'
    },
    toggleLabel: {
        fontSize: '0.9rem',
        fontWeight: 500,
        color: '#374151',
    },
    switch: {
        position: 'relative',
        display: 'inline-block',
        width: 48,
        height: 24,
    },
    slider: {
        position: 'absolute',
        cursor: 'pointer',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#ccc',
        transition: '.4s',
        borderRadius: 24,
        ':before': {
            position: 'absolute',
            content: "''",
            height: 20,
            width: 20,
            left: 2,
            bottom: 2,
            backgroundColor: 'white',
            transition: '.4s',
            borderRadius: '50%'
        }
    }
};

export default ConfigurationScreen;